import React, { useId, useState } from 'react'
import QRCode from 'react-qr-code'
import { I2FaSetup } from 'types'
import api from 'helpers/api'
import { downloadTextToClient } from 'helpers/fileDownload'
import { useApplicationStore } from 'hooks/application'
import { useAuth } from 'hooks/auth'
import Button from 'components/button'
import Dialog from 'components/dialog'
import Field from 'components/field'
import Input from 'components/input'
import { Copy, Download } from 'components/icons'
import { BLUE_BUTTON } from 'components/save-buttons'
import { generateRecoveryCodeTextFileContents } from 'views/security/utils'
import { FIELD, HINT, LABEL } from './changePasswordDialog'

/** Copies text, saying whether it worked: a browser without a clipboard throws straight away. */
const useCopy = () => {
  const { setSnackbarMessage } = useApplicationStore()
  return (text: string, what: string) =>
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(text))
      .then(() => setSnackbarMessage({ status: 'success', message: `${what} copied to clipboard` }))
      .catch(() => setSnackbarMessage({ status: 'error', message: `The ${what.toLowerCase()} couldn't be copied` }))
}

/** The code field both dialogs ask for: the six digits the authenticator app shows. */
const CodeField: React.FC<{ id: string; value: string; onChange: (code: string) => void }> = ({
  id,
  value,
  onChange
}) => (
  <Field label="Code" htmlFor={id} className={FIELD} labelClassName={LABEL}>
    <Input
      id={id}
      inputMode="numeric"
      autoComplete="one-time-code"
      placeholder="123456"
      value={value}
      onChange={(event) => onChange(event.target.value.trim())}
    />
  </Field>
)

/**
 * Turns on multi-factor authentication, in two steps of one dialog: scan the QR code into an authenticator app
 * and enter the code it shows; then the recovery codes, to copy or download, shown this once.
 */
export const TurnOnTwoFactorDialog: React.FC<{ setup: I2FaSetup; onClose: () => void }> = ({ setup, onClose }) => {
  const { user, updateUser } = useAuth()
  const { displayErrorMessage } = useApplicationStore()
  const copy = useCopy()
  const id = useId()
  const [code, setCode] = useState('')
  const [saving, setSaving] = useState(false)
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>()

  const turnOn = async () => {
    setSaving(true)
    let codes: string[]
    try {
      const response = await api.complete2FaSetup(code)
      codes = response.data
    } catch (error) {
      setSaving(false)
      displayErrorMessage(error)
      return
    }
    setSaving(false)
    setRecoveryCodes(codes)
    await updateUser({ is2FaEnabled: true })
  }

  if (recoveryCodes)
    return (
      // Not dismissible: closing it loses the codes for good, so it closes only once they're saved.
      <Dialog
        open
        dismissible={false}
        onOpenChange={(open) => !open && onClose()}
        title="Save Your Recovery Codes"
        description="Multi-factor authentication is on. If your authenticator app is unavailable, sign in with one of these codes instead. Each code can be used once, and they are not shown again."
        footer={
          <Button internalType="outline" className={BLUE_BUTTON} onClick={onClose}>
            I&apos;ve Saved My Codes
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          <ol
            className="grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-lg border border-divider bg-surface-sunken px-4 py-3 font-mono text-[13px] text-body"
            aria-label="Recovery codes"
          >
            {recoveryCodes.map((each, index) => (
              <li key={each} className="flex gap-2">
                <span className="select-none text-faint">{String(index + 1).padStart(2, '0')}</span>
                {each}
              </li>
            ))}
          </ol>
          <div className="flex gap-2">
            <Button internalType="outline" onClick={() => copy(recoveryCodes.join('\n'), 'Recovery codes')}>
              <Copy />
              Copy Codes
            </Button>
            <Button
              internalType="outline"
              onClick={() =>
                user &&
                downloadTextToClient('recovery-codes.txt', generateRecoveryCodeTextFileContents(user, recoveryCodes))
              }
            >
              <Download />
              Download Codes
            </Button>
          </div>
        </div>
      </Dialog>
    )

  // While the code is checked it stays open, so the recovery codes it brings back aren't lost.
  return (
    <Dialog
      open
      dismissible={!saving}
      onOpenChange={(open) => !open && !saving && onClose()}
      title="Turn On Multi-Factor Authentication"
      description="Scan the QR code with an authenticator app, such as Google Authenticator or Microsoft Authenticator, then enter the six-digit code the app shows."
      footer={
        <>
          <Button internalType="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            internalType="outline"
            className={BLUE_BUTTON}
            isLoading={saving}
            disabled={code === ''}
            onClick={turnOn}
          >
            Turn On
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-4">
          {/* On white in either theme, so any authenticator app can read it. */}
          <div className="shrink-0 rounded-lg border border-divider bg-white p-2">
            <QRCode value={setup.url} size={128} title="QR code for your authenticator app" />
          </div>
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className={LABEL}>Setup key</span>
            <p className={HINT}>For an app that cannot scan the QR code, enter this key instead.</p>
            <span className="group/key flex items-center gap-1">
              {/* In fours, as authenticator apps show it, so it can be read across and typed in. */}
              <span className="font-mono text-[13px] text-body">{setup.secret.match(/.{1,4}/g)?.join(' ')}</span>
              <Button
                internalType="transparent"
                aria-label="Copy setup key"
                className="size-6 shrink-0 p-0 [&_svg]:size-3.5"
                onClick={() => copy(setup.secret, 'Setup key')}
              >
                <Copy />
              </Button>
            </span>
          </div>
        </div>
        <CodeField id={`${id}-code`} value={code} onChange={setCode} />
      </div>
    </Dialog>
  )
}

/** Turns off multi-factor authentication, once a code from the authenticator app confirms it. */
export const TurnOffTwoFactorDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { updateUser } = useAuth()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const id = useId()
  const [code, setCode] = useState('')
  const [saving, setSaving] = useState(false)

  const turnOff = async () => {
    setSaving(true)
    try {
      await api.disable2FaSetup(code)
    } catch (error) {
      setSaving(false)
      displayErrorMessage(error)
      return
    }
    setSaving(false)
    await updateUser({ is2FaEnabled: false })
    setSnackbarMessage({ status: 'success', message: 'Multi-factor authentication was turned off' })
    onClose()
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Turn Off Multi-Factor Authentication?"
      description="Enter the code shown in your authenticator app to confirm. Signing in will then need only your password, and hubs that require multi-factor authentication cannot be opened."
      footer={
        <>
          <Button internalType="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button internalType="danger" isLoading={saving} disabled={code === ''} onClick={turnOff}>
            Turn Off
          </Button>
        </>
      }
    >
      <CodeField id={`${id}-code`} value={code} onChange={setCode} />
    </Dialog>
  )
}
